import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
    Mail,
    Zap,
    CheckCircle2,
    ArrowRight,
    Shield,
    Gift,
    Sparkles,
    Copy,
    Check,
    Flame,
    Gamepad2,
    Lock,
    Terminal,
    Cpu,
} from 'lucide-react';
import gsap from 'gsap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { subscribeToNewsletter } from '../../redux/slice/subscriber.slice';

const PERKS = [
    {
        icon: Gift,
        badge: '15% VOUCHER',
        title: 'INSTANT WELCOME BONUS',
        desc: 'Receive an exclusive 15% discount code right after transmission.',
        accent: 'from-cyber-pink/20 to-cyber-purple/10',
        border: 'border-cyber-pink/40',
        textAccent: 'text-cyber-pink',
        glow: 'rgba(255,0,122,0.3)',
    },
    {
        icon: Zap,
        badge: 'ZERO-DAY',
        title: 'FLASH DROP ALERTS',
        desc: 'Get priority 2-hour early access before public flash sales go live.',
        accent: 'from-cyber-blue/20 to-cyber-cyan/10',
        border: 'border-cyber-blue/40',
        textAccent: 'text-cyber-blue',
        glow: 'rgba(0,240,255,0.3)',
    },
    {
        icon: Gamepad2,
        badge: 'FREE LOOT',
        title: 'WEEKLY GAME KEY DROPS',
        desc: 'Automatic entry into weekly high-tier Steam & console game giveaways.',
        accent: 'from-cyber-purple/20 to-cyber-pink/10',
        border: 'border-cyber-purple/40',
        textAccent: 'text-cyber-purple',
        glow: 'rgba(189,0,255,0.3)',
    },
    {
        icon: Shield,
        badge: 'RADAR',
        title: 'PRICE DROP TELEMETRY',
        desc: 'Direct notifications when wishlist games hit all-time low price points.',
        accent: 'from-cyber-green/20 to-cyber-blue/10',
        border: 'border-cyber-green/40',
        textAccent: 'text-cyber-green',
        glow: 'rgba(57,255,20,0.3)',
    },
];

const QUICK_DOMAINS = ['@gmail.com', '@outlook.com', '@icloud.com', '@yahoo.com'];

const NewsletterSchema = Yup.object().shape({
    email: Yup.string()
        .email('Please enter a valid email address')
        .required('Email address is required'),
});

export default function NewsletterSignup() {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.subscriber);

    const sectionRef = useRef(null);
    const canvasRef = useRef(null);
    const cardRef = useRef(null);
    const successCardRef = useRef(null);

    const [copied, setCopied] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [mousePos, setMousePos] = useState({ x: 0, y: 0, active: false });

    // ── Formik Setup ──
    const formik = useFormik({
        initialValues: { email: '' },
        validationSchema: NewsletterSchema,
        onSubmit: async (values, { resetForm, setStatus }) => {
            try {
                await dispatch(subscribeToNewsletter(values.email.trim())).unwrap();
                setUserEmail(values.email.trim());
                setStatus('success');
                resetForm();
                toast.success('Access Granted! 15% VIP Coupon generated below.');
            } catch (err) {
                toast.error(err || 'Transmission failed. Please check your network and try again.');
            }
        },
    });

    const isSuccess = formik.status === 'success';

    // ── Mouse Parallax on Main Card ──
    const handleMouseMove = useCallback((e) => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        setMousePos({ x, y, active: true });
    }, []);

    const handleMouseLeave = useCallback(() => {
        setMousePos({ x: 0, y: 0, active: false });
    }, []);

    // ── Quick-Fill Domain Pills ──
    const handleQuickDomain = (domain) => {
        const current = formik.values.email.trim();
        if (!current) {
            formik.setFieldValue('email', domain);
            return;
        }
        if (current.includes('@')) {
            const prefix = current.split('@')[0];
            formik.setFieldValue('email', `${prefix}${domain}`);
        } else {
            formik.setFieldValue('email', `${current}${domain}`);
        }
    };

    // ── Copy Voucher Code ──
    const handleCopyCode = () => {
        navigator.clipboard.writeText('CUBE15');
        setCopied(true);
        toast.success('VIP Code "CUBE15" copied to clipboard!');
        setTimeout(() => setCopied(false), 2500);
    };

    // ── Success State GSAP Reveal ──
    useEffect(() => {
        if (isSuccess && successCardRef.current) {
            gsap.fromTo(
                successCardRef.current,
                { opacity: 0, scale: 0.88, y: 30 },
                { opacity: 1, scale: 1, y: 0, duration: 0.65, ease: 'back.out(1.8)' }
            );
        }
    }, [isSuccess]);

    return (
        <section
            id="newsletter-section"
            ref={sectionRef}
            className="py-8 sm:py-12 relative bg-transparent"
        >
            <div className="container mx-auto relative z-10 max-w-5xl">
                {/* Main Interactive Hologram Card */}
                <div
                    ref={cardRef}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-[#0e0e1a]/95 via-[#080812]/90 to-[#05050a]/95 backdrop-blur-2xl p-6 sm:p-10 lg:p-14 overflow-hidden shadow-[0_20px_70px_rgba(0,0,0,0.85)] group"
                    style={{
                        transform: mousePos.active
                            ? `perspective(1000px) rotateX(${mousePos.y * -4}deg) rotateY(${mousePos.x * 4}deg)`
                            : 'perspective(1000px) rotateX(0deg) rotateY(0deg)',
                        transition: mousePos.active ? 'transform 0.1s ease-out' : 'transform 0.5s ease-out',
                    }}
                >                    

                    {/* Canvas for 3D Holographic Cyber Cube Background Overlay */}
                    <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-35 sm:opacity-55 transition-opacity duration-500 group-hover:opacity-80">
                        <canvas ref={canvasRef} className="w-full h-full block" />
                    </div>

                    {/* Content Grid */}
                    <div className="relative z-10 flex flex-col items-center text-center max-w-3xl mx-auto">
                        {/* Title Header */}
                        <h2 className="font-cyber font-black text-2xl sm:text-4xl lg:text-5xl uppercase tracking-wider text-white mb-4 leading-tight">
                            Join our{' '}
                            <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">
                                Community
                            </span>
                        </h2>

                        {/* Subtitle */}
                        <p className="text-gray-400 font-sans max-w-2xl text-xs sm:text-sm mx-auto mb-8 sm:mb-10 leading-relaxed">
                            Subscribe for exclusive deals, new game releases, early access, and weekly gaming updates delivered to your inbox.
                        </p>

                        {/* Interactive Form or Success Celebration Pass */}
                        <div className="w-full max-w-xl mx-auto">
                            {isSuccess ? (
                                <div
                                    ref={successCardRef}
                                    className="relative rounded-2xl border border-cyber-green/40 bg-gradient-to-br from-[#0c1f15]/90 via-[#07130d]/80 to-[#040a06]/90 p-6 sm:p-8 backdrop-blur-xl shadow-[0_0_40px_rgba(57,255,20,0.2)] text-center"
                                >
                                    {/* Success Glow Ring */}
                                    <div className="w-16 h-16 rounded-2xl bg-cyber-green/20 border border-cyber-green/50 flex items-center justify-center mx-auto mb-4 shadow-[0_0_25px_rgba(57,255,20,0.3)] animate-bounce">
                                        <CheckCircle2 className="w-9 h-9 text-cyber-green" />
                                    </div>

                                    <span className="inline-block px-3 py-1 rounded-full bg-cyber-green/20 text-cyber-green text-[10px] font-cyber font-bold tracking-widest uppercase mb-2">
                                        TRANSMISSION VERIFIED
                                    </span>
                                    <h3 className="font-cyber font-extrabold text-2xl text-white uppercase tracking-wider mb-2">
                                        VIP PASS UNLOCKED
                                    </h3>
                                    <p className="text-gray-300 text-xs sm:text-sm font-sans mb-6">
                                        Signal confirmed for <span className="text-cyber-green font-mono font-medium">{userEmail}</span>.
                                        Check your inbox for your welcome package and download links.
                                    </p>

                                    {/* Holographic Coupon Reward Card */}
                                    <div className="relative rounded-xl border border-white/10 bg-white/5 p-4 sm:p-5 mb-5 flex flex-col sm:flex-row items-center justify-between gap-4">
                                        <div className="text-left">
                                            <p className="text-[10px] font-cyber text-gray-400 tracking-wider uppercase">
                                                YOUR 15% OFF VIP COUPON CODE
                                            </p>
                                            <p className="text-xl font-cyber font-black tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-cyber-green via-cyber-cyan to-white">
                                                CUBE15
                                            </p>
                                            <p className="text-[10px] text-gray-400 font-mono mt-0.5">
                                                Valid on all PC, Console & VR purchases at checkout.
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={handleCopyCode}
                                            className="w-full sm:w-auto px-5 py-2.5 rounded-xl font-cyber font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 bg-gradient-to-r from-cyber-green/30 to-cyber-cyan/30 hover:from-cyber-green/50 hover:to-cyber-cyan/50 border border-cyber-green/50 text-white transition-all shadow-[0_0_15px_rgba(57,255,20,0.2)] active:scale-95"
                                        >
                                            {copied ? (
                                                <>
                                                    <Check className="w-4 h-4 text-cyber-green" />
                                                    COPIED!
                                                </>
                                            ) : (
                                                <>
                                                    <Copy className="w-4 h-4" />
                                                    COPY CODE
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {/* Reset / Transmit Another Button */}
                                    <button
                                        type="button"
                                        onClick={() => formik.setStatus(null)}
                                        className="text-[11px] font-mono text-gray-400 hover:text-white underline underline-offset-4 transition-colors"
                                    >
                                        Transmit another address →
                                    </button>
                                </div>
                            ) : (
                                <form noValidate onSubmit={formik.handleSubmit} className="flex flex-col gap-3">
                                    {/* Input & Submit Bar */}
                                    <div className={`relative flex flex-col md:flex-row items-stretch gap-2 sm:gap-2.5 p-1.5 sm:p-2 rounded-2xl bg-[#080812]/90 border ${formik.touched.email && formik.errors.email
                                        ? 'border-red-500/80 shadow-[0_0_20px_rgba(239,68,68,0.25)]'
                                        : 'border-white/15 focus-within:border-cyan-400/80 shadow-[0_0_20px_rgba(0,0,0,0.6)] focus-within:shadow-[0_0_25px_rgba(0,240,255,0.25)]'
                                        } transition-all duration-300`}>
                                        <div className="flex-1 min-w-0 relative flex items-center py-1 md:py-0">
                                            <div className={`pl-3 sm:pl-3.5 pr-2 pointer-events-none ${formik.touched.email && formik.errors.email ? 'text-red-400' : 'text-cyan-400/70'} shrink-0`}>
                                                <Terminal className="w-4 h-4 sm:w-5 sm:h-5" />
                                            </div>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formik.values.email}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                placeholder="ENTER YOUR TRANSMISSION EMAIL..."
                                                className="w-full min-w-0 bg-transparent py-2.5 sm:py-3.5 pr-3 sm:pr-4 text-white text-xs sm:text-sm font-mono placeholder:text-gray-500 placeholder:font-sans focus:outline-none"
                                                disabled={loading}
                                                autoComplete="email"
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="group/btn relative w-full md:w-auto px-5 sm:px-7 py-3 sm:py-3.5 rounded-xl overflow-hidden font-cyber font-bold text-xs sm:text-sm uppercase tracking-wider text-black bg-gradient-to-r from-cyber-cyan via-cyber-blue to-cyber-purple hover:opacity-95 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-[0_0_20px_rgba(0,240,255,0.4)] flex items-center justify-center gap-2 whitespace-nowrap shrink-0"
                                        >
                                            {/* Button Light Shimmer Streak */}
                                            <div className="absolute inset-0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>

                                            {loading ? (
                                                <span className="flex items-center gap-2">
                                                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></span>
                                                    <span>TRANSMITTING...</span>
                                                </span>
                                            ) : (
                                                <>
                                                    <span>SUBSCRIBE</span>
                                                    <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1 shrink-0" />
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {/* Quick-Fill Domain Suggestion Pills */}
                                    <div className="flex flex-wrap items-center justify-center gap-1.5 pt-1">
                                        <span className="text-[10px] font-mono text-gray-500 mr-1 flex items-center gap-1">
                                            <Sparkles className="w-3 h-3 text-cyan-400" /> QUICK:
                                        </span>
                                        {QUICK_DOMAINS.map((domain) => (
                                            <button
                                                key={domain}
                                                type="button"
                                                onClick={() => handleQuickDomain(domain)}
                                                className="px-2.5 py-1 rounded-lg bg-white/5 hover:bg-cyan-500/20 border border-white/10 hover:border-cyan-400/40 text-[10px] font-mono text-gray-400 hover:text-cyan-300 transition-all duration-200"
                                            >
                                                {domain}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Inline Validation Warning */}
                                    {formik.touched.email && formik.errors.email && (
                                        <p className="text-red-400 text-xs sm:text-sm font-sans font-medium tracking-wide flex items-center justify-center gap-1.5 mt-1">
                                            <span>⚠</span> {formik.errors.email}
                                        </p>
                                    )}
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
